import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { DashboardInfo } from '@models/Dashboard/dashboardInfo.model';
import { DashboardWizardState } from '@models/Dashboard/dashboardWizard.model';
import { UserGroup } from '@models/User/usergroup.model';
import { ImageFavoriteInfo } from '@models/ImageFavorite/imageFavoriteInfo.model';
import { ImageFavoriteImage } from '@models/ImageFavorite/imageFavoriteImage.model';
import { ImageLabelInfo } from '@models/ImageFavorite/imageLabelInfo.model';
import { ImageSourceType } from '@models/ImageFavorite/imageFavorite.enums';
import { SvMetadataInfo } from '@models/Scoutview/scoutviewInfo.model';
import { ImageFavoriteService } from '@services/imageFavorite.service';
import { ScoutviewService } from '@services/scoutview.service';
import { ImageFavoriteGateway } from 'app/shared/components/image-favorite/image-favorite-edit-modal/image-favorite-edit-modal.component';

// The Image Favorite flow: steps 2-5 of the create wizard.
//   2 - background source (static upload or dynamic gateway image)
//   3 - the image itself (upload, or gateway + optional media tag)
//   4 - labels (edited on the right-pane preview, held in wizard state)
//   5 - save (name, access group, new/existing dashboard)
// Nothing is persisted here; the container saves everything on Finish.
@Component({
  standalone: false,
  selector: 'image-favorite-wizard-flow',
  templateUrl: './image-favorite-wizard-flow.component.html',
  styleUrls: ['../chart-favorite-flow/chart-favorite-wizard-flow.component.scss', './image-favorite-wizard-flow.component.scss']
})
export class ImageFavoriteWizardFlowComponent implements OnInit, OnChanges {
  @Input() step = 2;
  @Input() state: DashboardWizardState;
  @Input() dashboards: DashboardInfo[] = [];
  @Input() associatedUserGroups: UserGroup[] = [];
  @Input() username = '';
  @Input() gateways: ImageFavoriteGateway[] = [];
  // When set, the wizard edits this existing image favorite (pre-load its values).
  @Input() editImageFavorite: ImageFavoriteInfo = null;
  @Input() isEditMode = false;

  @Output() stepValidChange = new EventEmitter<boolean>();

  readonly SourceType = ImageSourceType;

  readonly userGroupConfig: any = {
    displayKey: 'name',
    search: true,
    height: '220px',
    placeholder: 'Select Access Group',
    searchOnKey: 'name'
  };

  // Media tags seen on the selected gateway's recent images (any tag may be typed in).
  tagSuggestions: string[] = [];
  previewLoading = false;
  previewMissing = false;

  private existingFavoriteNames: string[] = [];

  constructor(private imageFavoriteService: ImageFavoriteService,
              private scoutviewService: ScoutviewService,
              private toastr: ToastrService) {}

  ngOnInit(): void {
    this.loadImageFavoriteNames();
    if (this.isEditMode && this.editImageFavorite != undefined) {
      this.applyEditImageFavorite();
    } else if (this.m.favoriteUserGroup == undefined) {
      this.m.favoriteUserGroup = this.associatedUserGroups.find(u => u.id == -1) ?? null;
    }
    if (this.m.imageSourceType === ImageSourceType.Dynamic && this.m.gatewaySourceId != undefined) {
      this.loadGatewayTags();
    }
    this.recomputeValidity();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['step'] && !changes['step'].firstChange) {
      this.recomputeValidity();
    }
  }

  // Shorthand to the image-favorite slice of the shared wizard state.
  get m() {
    return this.state.imageFavorite;
  }

  // True when the entered name already exists (case-insensitive). When editing,
  // the favorite's own current name is allowed.
  get favoriteNameTaken(): boolean {
    const name = (this.m.favoriteName || '').trim().toLowerCase();
    if (name === '') { return false; }
    if (this.isEditMode && this.editImageFavorite && name === (this.editImageFavorite.name || '').trim().toLowerCase()) {
      return false;
    }
    return this.existingFavoriteNames.includes(name);
  }

  get newDashboardNameTaken(): boolean {
    const name = (this.m.newDashboardName || '').trim().toLowerCase();
    if (this.m.dashboardTargetMode !== 'new' || name === '') {
      return false;
    }
    return this.dashboards.some(d => d.name && d.name.trim().toLowerCase() === name);
  }

  get dynamicLabelCount(): number {
    return this.m.labels.filter(l => l.isDynamic).length;
  }

  // ---------- data loading ----------

  private loadImageFavoriteNames(): void {
    this.imageFavoriteService.getImageFavorites().subscribe(
      favorites => {
        this.existingFavoriteNames = (favorites || []).map(f => (f.name || '').trim().toLowerCase());
        this.recomputeValidity();
      },
      () => { /* non-fatal */ }
    );
  }

  // Seed the wizard state from the favorite being edited (labels are copies so
  // Cancel discards any changes).
  private applyEditImageFavorite(): void {
    const e = this.editImageFavorite;
    this.m.imageSourceType = e.imageSourceType;
    this.m.gatewaySourceId = e.gatewaySourceId;
    this.m.imageTag = e.imageTag;
    this.m.favoriteName = e.name;
    this.m.favoriteUserGroup = this.associatedUserGroups.find(u => u.id == (e.userGroupId ?? -1))
      ?? this.associatedUserGroups.find(u => u.id == -1) ?? null;

    this.previewLoading = true;
    this.imageFavoriteService.getImage(e.id).subscribe(
      (result: ImageFavoriteImage | null) => {
        this.previewLoading = false;
        const src = this.toDataUrl(result);
        if (e.imageSourceType === ImageSourceType.Static) {
          this.m.existingStaticImageSrc = src;
        }
        if (this.m.imageSourceType === e.imageSourceType && this.m.file == undefined) {
          this.m.previewImageSrc = src;
        }
        this.previewMissing = src == null;
        this.recomputeValidity();
      },
      () => { this.previewLoading = false; this.toastr.error('Unable to load the image favorite\'s image.'); }
    );

    this.imageFavoriteService.getImageLabels(e.id).subscribe(
      (labels: ImageLabelInfo[] | null) => {
        this.m.labels = (labels ?? []).map(l => ({ ...l }));
        this.m.originalLabelIds = this.m.labels.map(l => l.id);
      },
      () => this.toastr.error('Unable to load the image favorite\'s labels.')
    );
  }

  private loadGatewayTags(): void {
    const sourceId = this.m.gatewaySourceId;
    this.tagSuggestions = [];
    if (sourceId == undefined || sourceId === '') {
      return;
    }
    this.scoutviewService.getSvMetadatasForSource({ sourceId, newest: true, take: 50 }).subscribe(
      (metadatas: SvMetadataInfo[] | null) => {
        if (this.m.gatewaySourceId !== sourceId) {
          return;
        }
        const tags = new Set<string>();
        (metadatas ?? []).forEach(md => (md.tags ?? []).forEach(t => { if (t != undefined && t.trim() !== '') { tags.add(t.trim()); } }));
        this.tagSuggestions = [...tags].sort((a, b) => a.localeCompare(b));
      },
      () => { /* non-fatal: tags may still be typed in */ }
    );
  }

  private loadPreviewImage(): void {
    const sourceId = this.m.gatewaySourceId;
    const tag = this.m.imageTag;
    this.m.previewImageSrc = null;
    this.previewMissing = false;
    if (sourceId == undefined || sourceId === '') {
      return;
    }
    this.previewLoading = true;
    this.imageFavoriteService.getPreviewImage(sourceId, tag).subscribe(
      (result: ImageFavoriteImage | null) => {
        if (this.m.gatewaySourceId !== sourceId || this.m.imageTag !== tag) {
          return;
        }
        this.previewLoading = false;
        this.m.previewImageSrc = this.toDataUrl(result);
        this.previewMissing = this.m.previewImageSrc == null;
      },
      () => { this.previewLoading = false; this.previewMissing = true; }
    );
  }

  private toDataUrl(result: ImageFavoriteImage | null): string | null {
    return (result != undefined && result.base64 != undefined && result.base64 !== '')
      ? `data:${result.contentType ?? 'image/jpeg'};base64,${result.base64}`
      : null;
  }

  // ---------- step handlers ----------

  onSourceTypeChange(): void {
    this.previewMissing = false;
    if (this.m.imageSourceType === ImageSourceType.Static) {
      this.m.previewImageSrc = this.m.fileDataUrl ?? this.m.existingStaticImageSrc;
    } else {
      this.loadGatewayTags();
      this.loadPreviewImage();
    }
    this.recomputeValidity();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files != undefined && input.files.length > 0 ? input.files[0] : null;
    this.m.file = file;
    this.m.fileDataUrl = null;
    this.m.previewImageSrc = this.m.existingStaticImageSrc;
    if (file != undefined) {
      const reader = new FileReader();
      reader.onload = () => {
        this.m.fileDataUrl = reader.result as string;
        this.m.previewImageSrc = this.m.fileDataUrl;
        this.recomputeValidity();
      };
      reader.readAsDataURL(file);
    }
    this.recomputeValidity();
  }

  onGatewayChange(): void {
    this.loadGatewayTags();
    this.loadPreviewImage();
    this.recomputeValidity();
  }

  onTagChange(): void {
    if (this.m.imageTag != undefined && this.m.imageTag.trim() === '') {
      this.m.imageTag = null;
    }
    this.loadPreviewImage();
  }

  onSaveFieldChanged(): void {
    this.recomputeValidity();
  }

  // ---------- validation (gates the container's Next / Finish) ----------

  private recomputeValidity(): void {
    let valid = false;
    switch (this.step) {
      case 2:
        valid = this.m.imageSourceType === ImageSourceType.Static || this.m.imageSourceType === ImageSourceType.Dynamic;
        break;
      case 3:
        valid = this.m.imageSourceType === ImageSourceType.Static
          ? (this.m.fileDataUrl != undefined || this.m.existingStaticImageSrc != undefined)
          : (this.m.gatewaySourceId != undefined && this.m.gatewaySourceId !== '');
        break;
      case 4:
        // Labels are optional; a dynamic label needs its sensor + reading type.
        valid = this.m.labels.every(l => !l.isDynamic || (l.sensorID != undefined && l.sensorReadingTypeID != undefined));
        break;
      case 5: {
        const nameOk = !!this.m.favoriteName && this.m.favoriteName.trim().length > 0 && !this.favoriteNameTaken;
        valid = this.isEditMode
          ? nameOk
          : nameOk && (this.m.dashboardTargetMode === 'existing'
              ? this.m.targetDashboard != null
              : !!this.m.newDashboardName && this.m.newDashboardName.trim().length > 0 && !this.newDashboardNameTaken);
        break;
      }
      default:
        valid = true;
    }
    this.stepValidChange.emit(valid);
  }

  // Labels change on the preview (owned by the container); re-validate step 4.
  onLabelsChanged(): void {
    this.recomputeValidity();
  }
}
